import { SVGProps } from 'react';

import type { IconProps as IconsaxProps } from 'iconsax-react';

/**
 * Custom icons accept the same prop signature as iconsax-react components
 * so they can be used interchangeably from the registry.
 *
 * `variant` is an iconsax-style 'Linear' | 'Bold' | … — for custom icons we
 * only honor 'Linear' vs 'Bold'; other variants fall back to the linear path.
 */
export type CustomIconProps = Omit<SVGProps<SVGSVGElement>, 'color'> &
  Pick<IconsaxProps, 'size' | 'color' | 'variant'>;

const baseSvgProps = (size: number | string = 24, color = 'currentColor') => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: color,
  xmlns: 'http://www.w3.org/2000/svg',
});

export const YoutubeLogo = ({
  size,
  color,
  variant: _variant,
  ...rest
}: CustomIconProps) => (
  <svg {...baseSvgProps(size, color)} {...rest}>
    <g clipPath="url(#mp-yt-clip)">
      <path d="M17 20.75H7C3.56 20.75 1.25 18.44 1.25 15V9C1.25 5.56 3.56 3.25 7 3.25H17C20.44 3.25 22.75 5.56 22.75 9V15C22.75 18.44 20.44 20.75 17 20.75ZM7 4.75C4.42 4.75 2.75 6.42 2.75 9V15C2.75 17.58 4.42 19.25 7 19.25H17C19.58 19.25 21.25 17.58 21.25 15V9C21.25 6.42 19.58 4.75 17 4.75H7Z" />
      <path d="M10.59 15.53c-.29 0-.57-.07-.82-.21-.58-.33-.92-1-.92-1.83v-2.96c0-.83.33-1.5.92-1.83.58-.33 1.33-.27 2.04.16l2.47 1.48c.68.41 1.07 1.02 1.07 1.67s-.39 1.26-1.07 1.67l-2.47 1.48c-.41.24-.83.37-1.22.37zm0-5.56c-.04 0-.07.01-.09.02-.07.04-.15.22-.15.53v2.96c0 .3.08.48.15.53.08.04.27.02.53-.14l2.47-1.48c.25-.15.34-.3.34-.39 0-.09-.09-.23-.34-.39l-2.47-1.48c-.18-.11-.34-.16-.44-.16z" />
    </g>
    <defs>
      <clipPath id="mp-yt-clip">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export const TakeTour = ({
  size,
  color,
  variant: _variant,
  ...rest
}: CustomIconProps) => (
  <svg {...baseSvgProps(size, color)} {...rest}>
    <g clipPath="url(#mp-tour-clip)">
      <path d="M17.56 17.97H6.44C2.46 17.97 1.25 16.76 1.25 12.78V6.44C1.25 2.46 2.46 1.25 6.44 1.25H8C8.41 1.25 8.75 1.59 8.75 2C8.75 2.41 8.41 2.75 8 2.75H6.44C3.3 2.75 2.75 3.3 2.75 6.44V12.77C2.75 15.91 3.3 16.46 6.44 16.46H17.55C20.69 16.46 21.24 15.91 21.24 12.77V11.88C21.24 11.47 21.58 11.13 21.99 11.13C22.4 11.13 22.74 11.47 22.74 11.88V12.77C22.75 16.76 21.54 17.97 17.56 17.97Z" />
      <path d="M12 22.75c-.41 0-.75-.34-.75-.75v-4.78c0-.41.34-.75.75-.75s.75.34.75.75V22c0 .41-.34.75-.75.75z" />
      <path d="M22 13.75H2c-.41 0-.75-.34-.75-.75s.34-.75.75-.75h20c.41 0 .75.34.75.75s-.34.75-.75.75z" />
      <path d="M16.5 22.75h-9c-.41 0-.75-.34-.75-.75s.34-.75.75-.75h9c.41 0 .75.34.75.75s-.34.75-.75.75z" />
      <path d="M17.86 10.12h-4.76c-1.57 0-2.59-1.02-2.59-2.59V4.01c0-1.52 1.23-2.75 2.75-2.75h4.6c1.43 0 2.59 1.16 2.59 2.59v3.68c0 1.43-1.16 2.59-2.59 2.59zm-4.6-7.36c-.69 0-1.25.56-1.25 1.25v3.52c0 .98.62 1.09 1.09 1.09h4.76c.6 0 1.09-.49 1.09-1.09V3.85c0-.6-.49-1.09-1.09-1.09h-4.6z" />
      <path d="M21.45 8.87c-.32 0-.65-.11-.97-.34l-1.21-.85c-.2-.14-.32-.37-.32-.61V4.31c0-.24.12-.47.32-.61l1.21-.85c.52-.37 1.08-.44 1.54-.2.46.24.72.74.72 1.38v3.33c0 .64-.26 1.14-.72 1.38-.17.09-.36.13-.57.13zM20.45 6.68l.8.56V4.14l-.8.56v1.98z" />
    </g>
    <defs>
      <clipPath id="mp-tour-clip">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

/**
 * Bare checkmark glyph — no enclosing circle/square. Use when the surrounding
 * UI already provides a colored circle/pill (the iconsax `TickCircle` would
 * render a circle-inside-a-circle in those cases).
 */
export const Tick = ({ size, color, variant, ...rest }: CustomIconProps) => {
  const stroke = color ?? 'currentColor';
  const isBold = variant === 'Bold';
  return (
    <svg
      width={size ?? 24}
      height={size ?? 24}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path
        d="M5 12.5l4.5 4.5L19 7"
        stroke={stroke}
        strokeWidth={isBold ? 3 : 2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

/**
 * Magpiie "revisions" — split-frames icon from the design system
 * (magpiie-ui-ux/ui_kits/app/Shared.jsx ICONS.revisions).
 */
export const RevisionsSplit = ({
  size,
  color,
  variant,
  ...rest
}: CustomIconProps) => {
  const isBold = variant === 'Bold';
  const fill = color ?? 'currentColor';
  return (
    <svg
      width={size ?? 24}
      height={size ?? 24}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      {isBold ? (
        <>
          <path
            d="M15 22c-.41 0-.75-.34-.75-.75v-18.5c0-.41.34-.75.75-.75s.75.34.75.75v18.5c0 .41-.34.75-.75.75z"
            fill={fill}
          />
          <path
            d="M18 20h-3V4h3c2.21 0 4 1.79 4 4v8c0 2.21-1.79 4-4 4z"
            fill={fill}
          />
          <path
            d="M6 4C3.79 4 2 5.79 2 8v8c0 2.21 1.79 4 4 4h5c.55 0 1-.45 1-1V5c0-.55-.45-1-1-1H6zm1.75 11.5c0 .41-.34.75-.75.75s-.75-.34-.75-.75v-7c0-.41.34-.75.75-.75s.75.34.75.75v7z"
            fill={fill}
            fillRule="evenodd"
            clipRule="evenodd"
          />
        </>
      ) : (
        <>
          <path
            d="M11.02 20.25H7.5c-.75 0-1.3-.03-1.77-.1-3.33-.37-3.98-2.35-3.98-5.65v-5c0-3.3.66-5.29 4.01-5.66.44-.06.99-.09 1.74-.09h3.46c.41 0 .75.34.75.75s-.34.75-.75.75H7.5c-.68 0-1.16.03-1.55.08-2.03.22-2.7.86-2.7 4.17v5c0 3.31.67 3.94 2.67 4.17.42.06.9.08 1.58.08h3.52c.41 0 .75.34.75.75s-.34.75-.75.75z"
            fill={fill}
          />
          <path
            d="M16.5 20.25h-1.48c-.41 0-.75-.34-.75-.75s.34-.75.75-.75h1.48c.68 0 1.16-.03 1.55-.08 2.03-.22 2.7-.86 2.7-4.17v-5c0-3.31-.67-3.94-2.67-4.17-.42-.06-.9-.08-1.58-.08h-1.48c-.41 0-.75-.34-.75-.75s.34-.75.75-.75h1.48c.75 0 1.3.03 1.77.1 3.33.37 3.98 2.35 3.98 5.65v5c0 3.3-.66 5.29-4.01 5.66-.44.06-.99.09-1.74.09z"
            fill={fill}
          />
          <path
            d="M15 22.75c-.41 0-.75-.34-.75-.75V2c0-.41.34-.75.75-.75s.75.34.75.75v20c0 .41-.34.75-.75.75z"
            fill={fill}
          />
          <path
            d="M8 16.25c-.41 0-.75-.34-.75-.75v-7c0-.41.34-.75.75-.75s.75.34.75.75v7c0 .41-.34.75-.75.75z"
            fill={fill}
          />
        </>
      )}
    </svg>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Design-system–exact icons
// SVG paths sourced verbatim from magpiie-ui-ux/ui_kits/app/Shared.jsx ICONS.
// Used for settings sidebar items so the rendered shapes match the reference.
// ─────────────────────────────────────────────────────────────────────────────

export const DSSpeedometer = ({
  size,
  color = 'currentColor',
  variant,
  ...rest
}: CustomIconProps) => {
  const sw = variant === 'Bold' ? 2.2 : 1.6;
  if (variant === 'Bold') {
    return (
      <svg
        width={size ?? 24}
        height={size ?? 24}
        viewBox="0 0 24 24"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...rest}
      >
        <path
          d="M12 2C6.49 2 2 6.49 2 12s4.49 10 10 10 10-4.49 10-10S17.51 2 12 2z"
          fill={color}
        />
        <path
          d="M7 10c1-3 4-5 7-5M16 14a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 8v2.5"
          stroke={color}
          strokeWidth={sw}
          fill="none"
        />
      </svg>
    );
  }
  return (
    <svg
      width={size ?? 24}
      height={size ?? 24}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      <path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM7 10c1-3 4-5 7-5M16 14a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 8v2.5" />
    </svg>
  );
};

export const DSUsers = ({
  size,
  color = 'currentColor',
  variant,
  ...rest
}: CustomIconProps) => {
  const sw = variant === 'Bold' ? 2.2 : 1.6;
  if (variant === 'Bold') {
    return (
      <svg
        width={size ?? 24}
        height={size ?? 24}
        viewBox="0 0 24 24"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...rest}
      >
        <path
          d="M9 2a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9zM15.5 4.5a3.5 3.5 0 1 0 .3 6.98l.14-.02a3.5 3.5 0 0 0-.44-6.96zM4.16 14.06c-3 2-3 5.28 0 7.26 3.42 2.29 9.03 2.29 12.45 0 2.99-2 2.99-5.28 0-7.26-3.43-2.28-9.03-2.28-12.45 0zM20.3 14.5a4.4 4.4 0 0 0-1.82-.5c.82.97.82 2.15 0 3.12a4.7 4.7 0 0 1-1.86.94c2.03.51 4.32.14 6-1.11 1.24-.92 1.24-2.43 0-3.35a4.4 4.4 0 0 0-1.9-.89"
          fill={color}
          stroke="none"
        />
      </svg>
    );
  }
  return (
    <svg
      width={size ?? 24}
      height={size ?? 24}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      <path d="M9.16 10.87c-.1-.01-.22-.01-.33 0a4.42 4.42 0 0 1-4.27-4.43A4.43 4.43 0 0 1 9 2a4.43 4.43 0 0 1 .16 8.87zM16.41 4a3.5 3.5 0 0 1 0 7c-.09 0-.19 0-.29-.02M4.16 14.56c-2.42 1.62-2.42 4.26 0 5.87 2.75 1.84 7.26 1.84 10.01 0 2.42-1.62 2.42-4.26 0-5.87-2.74-1.83-7.25-1.83-10.01 0zM18.34 20c.72-.15 1.4-.44 1.96-.87 1.56-1.17 1.56-3.1 0-4.27a6.17 6.17 0 0 0-1.93-.86" />
    </svg>
  );
};

export const DSBilling = ({
  size,
  color = 'currentColor',
  variant,
  ...rest
}: CustomIconProps) => {
  const sw = variant === 'Bold' ? 2.2 : 1.6;
  if (variant === 'Bold') {
    return (
      <svg
        width={size ?? 24}
        height={size ?? 24}
        viewBox="0 0 24 24"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...rest}
      >
        <path
          d="M16.45 1.25H7.54A3.81 3.81 0 0 0 3.75 5.04V21a.75.75 0 0 0 1.06.68l2.69-1.21 2.44 1.11a.74.74 0 0 0 .62 0l2.44-1.11 2.69 1.21A.75.75 0 0 0 16.75 21V4.98c0-.41-.34-.75-.75-.75h-.05.5zM8.38 11.75H7.5a.75.75 0 1 1 0-1.5h.88a.75.75 0 1 1 0 1.5zm0-4H7.5a.75.75 0 1 1 0-1.5h.88a.75.75 0 1 1 0 1.5zm6.12 5H11a.75.75 0 1 1 0-1.5h3.5a.75.75 0 1 1 0 1.5zm0-3.5H11a.75.75 0 1 1 0-1.5h3.5a.75.75 0 1 1 0 1.5zM20.21 4.23h-2.46v17.51c.06.01.13.01.19.01.42-.01.81-.18 1.11-.48l1.61-1.6c.32-.32.49-.74.49-1.18V5.71c0-.82-.66-1.48-1.48-1.48z"
          fill={color}
          stroke="none"
        />
      </svg>
    );
  }
  return (
    <svg
      width={size ?? 24}
      height={size ?? 24}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      <path d="M11 8.5h3.5M11 12h3.5M4.5 21V4.98C4.5 3.33 5.86 2 7.54 2h8.91A3.05 3.05 0 0 1 19.5 4.98V21l-3.75-1.25L13 21l-2.75-1.25L7.5 22M7.5 11h.88M7.5 7h.88" />
    </svg>
  );
};

export const DSPlay = ({
  size,
  color = 'currentColor',
  variant,
  ...rest
}: CustomIconProps) => {
  const sw = variant === 'Bold' ? 2.2 : 1.6;
  const d =
    'M7.32 5.29v13.42c0 1.39 1.51 2.26 2.71 1.55l5.56-3.3 5.57-3.31c1.2-.71 1.2-2.42 0-3.13l-5.57-3.3-5.56-3.31c-1.2-.72-2.71.15-2.71 1.38z';
  if (variant === 'Bold') {
    return (
      <svg
        width={size ?? 24}
        height={size ?? 24}
        viewBox="0 0 24 24"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...rest}
      >
        <path d={d} fill={color} stroke="none" />
      </svg>
    );
  }
  return (
    <svg
      width={size ?? 24}
      height={size ?? 24}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      <path d={d} />
    </svg>
  );
};

export const DSUser = ({
  size,
  color = 'currentColor',
  variant,
  ...rest
}: CustomIconProps) => {
  const sw = variant === 'Bold' ? 2.2 : 1.6;
  if (variant === 'Bold') {
    return (
      <svg
        width={size ?? 24}
        height={size ?? 24}
        viewBox="0 0 24 24"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...rest}
      >
        <path
          d="M12 2a4.9 4.9 0 0 1 5 4.99A4.92 4.92 0 0 1 12.18 12h-.35A4.97 4.97 0 0 1 7 6.99 4.97 4.97 0 0 1 12 2zM20 17.5c0 2.5-2 4.5-4.5 4.5h-7A4.5 4.5 0 0 1 4 17.5c0-2.48 2.01-4.5 4.5-4.5h7c2.49 0 4.5 2.02 4.5 4.5z"
          fill={color}
          stroke="none"
        />
      </svg>
    );
  }
  return (
    <svg
      width={size ?? 24}
      height={size ?? 24}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zM20.59 22c0-3.87-3.85-7-8.59-7-4.74 0-8.59 3.13-8.59 7" />
    </svg>
  );
};

export const DSShield = ({
  size,
  color = 'currentColor',
  variant,
  ...rest
}: CustomIconProps) => {
  const sw = variant === 'Bold' ? 2.2 : 1.6;
  if (variant === 'Bold') {
    return (
      <svg
        width={size ?? 24}
        height={size ?? 24}
        viewBox="0 0 24 24"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...rest}
      >
        <path
          d="M20.51 4.48l-6.01-2.25c-1.39-.52-3.01-.52-4.4 0L4.09 4.48a3.91 3.91 0 0 0-2.49 3.5v6.31c0 2.16 1.41 4.92 3.09 6.18l5.18 3.87a4.27 4.27 0 0 0 4.96 0l5.18-3.87c1.68-1.27 3.09-4.02 3.09-6.18V7.98a3.93 3.93 0 0 0-2.49-3.5zm-3.74 6.94-5.03 5.03c-.15.15-.34.22-.53.22s-.39-.07-.53-.22l-2.42-2.42a.754.754 0 0 1 1.06-1.06l1.89 1.89 4.5-4.5c.29-.29.77-.29 1.06 0 .29.29.29.77 0 1.06z"
          fill={color}
          stroke="none"
        />
      </svg>
    );
  }
  return (
    <svg
      width={size ?? 24}
      height={size ?? 24}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      <path d="M10.49 2.23l-6 2.25a3.16 3.16 0 0 0-1.99 2.8v6.31c0 1.9 1.26 4.4 2.79 5.55l5.17 3.86a3.53 3.53 0 0 0 4.08 0l5.17-3.86c1.53-1.15 2.79-3.65 2.79-5.55V7.28a3.16 3.16 0 0 0-1.99-2.8l-6-2.25c-.99-.37-2.15-.37-3.02 0z" />
      <path d="m8.75 12 1.87 1.87 5.03-5.03" />
    </svg>
  );
};

export const DSArchive = ({
  size,
  color = 'currentColor',
  variant,
  ...rest
}: CustomIconProps) => {
  const sw = variant === 'Bold' ? 2.2 : 1.6;
  if (variant === 'Bold') {
    return (
      <svg
        width={size ?? 24}
        height={size ?? 24}
        viewBox="0 0 24 24"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...rest}
      >
        <path
          d="M21.04 5.3l-1.38-1.67A2.71 2.71 0 0 0 17.5 2.5h-11c-.93 0-1.74.36-2.27.99L2.96 5.3c-.34.51-.46.94-.46 1.65v11.05c0 1.65 1.35 3 3 3h13c1.65 0 3-1.35 3-3V6.95c0-.71-.12-1.14-.46-1.65ZM6.5 4h11c.37 0 .58.1.79.34l.95 1.16H4.76l.95-1.16C5.92 4.1 6.13 4 6.5 4Zm8.07 8.75H9.43a.75.75 0 0 1 0-1.5h5.14a.75.75 0 0 1 0 1.5Z"
          fill={color}
          stroke="none"
        />
      </svg>
    );
  }
  return (
    <svg
      width={size ?? 24}
      height={size ?? 24}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      <path d="M20.54 5.23l-1.39-1.68A2.18 2.18 0 0 0 17.5 3h-11C5.7 3 4.85 3.3 4.36 3.9L2.97 5.23A2.1 2.1 0 0 0 2.5 6.78V19c0 1.1.9 2 2 2h15c1.1 0 2-.9 2-2V6.78c0-.56-.18-1.13-.46-1.55ZM3.17 5.6 4.55 4.3c.2-.24.5-.3.95-.3h11c.45 0 .79.1.99.34l1.37 1.66c.05.06.09.13.12.2H3.05c.03-.07.07-.14.12-.2Z" />
      <path d="M2.5 6.95h19M9.43 12h5.14M12 21V6.95" />
    </svg>
  );
};

export const DSFolder = ({
  size,
  color = 'currentColor',
  variant,
  ...rest
}: CustomIconProps) => {
  const sw = variant === 'Bold' ? 2.2 : 1.6;
  if (variant === 'Bold') {
    return (
      <svg
        width={size ?? 24}
        height={size ?? 24}
        viewBox="0 0 24 24"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...rest}
      >
        <path
          d="M17 5.5h-4.31l-1.78-2.37c-.42-.56-1.09-.88-1.79-.88H7c-2.76 0-5 2.24-5 5v9.5c0 2.76 2.24 5 5 5h10c2.76 0 5-2.24 5-5v-6.25c0-2.76-2.24-5-5-5z"
          fill={color}
          stroke="none"
        />
      </svg>
    );
  }
  return (
    <svg
      width={size ?? 24}
      height={size ?? 24}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      <path d="M22 11v5c0 4-1 5-5 5H7c-4 0-5-1-5-5V8c0-4 1-5 5-5h1.5c1.5 0 1.83.44 2.4 1.2l1.5 2c.38.5.6.8 1.6.8H17c4 0 5 1 5 5z" />
    </svg>
  );
};
